import {
  highestPriorityStatus,
  type Timesheet,
  type TimesheetStatus,
} from '@clockwise/clients/timesheets';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { formatHours, shortDayName, sumHours, weekDates } from './timesheet-week';

export type DayStatus = TimesheetStatus | 'empty';

export interface DayCell {
  date: string;
  status: DayStatus;
  hours: number;
  count: number;
}

export function deriveDayCells(entries: readonly Timesheet[], weekStart: string): DayCell[] {
  return weekDates(weekStart).map((date) => {
    const dayEntries = entries.filter((e) => e.date === date);
    if (dayEntries.length === 0) {
      return { date, status: 'empty', hours: 0, count: 0 };
    }
    const statuses = new Set(dayEntries.map((e) => e.status));
    // `highestPriorityStatus` consults the exhaustive
    // TIMESHEET_STATUS_PRIORITY record from @clockwise/shared — adding a
    // new TimesheetStatus is a compile error there, not a silent
    // misclassification here.
    const status = highestPriorityStatus(statuses) ?? 'draft';
    const hours = sumHours(dayEntries.map((e) => e.hours ?? 0));
    return { date, status, hours, count: dayEntries.length };
  });
}

/** Tooltip-safe hours display — uses formatHours' 2dp rounding and
 *  trailing-zero strip so floating-point sums like 1.1 + 2.2 don't render
 *  as "3.3000000000000003h". Returns "0h" for zero/empty to keep cells
 *  with no logged hours readable. */
function tooltipHours(hours: number): string {
  if (!Number.isFinite(hours) || hours === 0) return '0h';
  return formatHours(hours);
}

/** Saturated dot color + label per day status, the single source shared by
 *  the per-day status strip and the week-view find bar's status toggles (so the
 *  two never drift on what colour a status is). `empty` is strip-only. */
export const STATUS_STYLES: Record<DayStatus, { dot: string; label: string }> = {
  empty: {
    dot: 'bg-transparent border border-dashed border-border',
    label: 'Empty',
  },
  draft: {
    dot: 'bg-muted-foreground/30 border border-muted-foreground/40',
    label: 'Draft',
  },
  submitted: {
    dot: 'bg-blue-500 border border-blue-600 dark:bg-blue-400 dark:border-blue-300',
    label: 'Submitted',
  },
  approved: {
    dot: 'bg-green-500 border border-green-600 dark:bg-green-400 dark:border-green-300',
    label: 'Approved',
  },
  rejected: {
    dot: 'bg-red-500 border border-red-600 dark:bg-red-400 dark:border-red-300',
    label: 'Rejected',
  },
  failed: {
    dot: 'bg-yellow-500 border border-yellow-600 dark:bg-yellow-400 dark:border-yellow-300',
    label: 'Needs review',
  },
};

interface Props {
  entries: readonly Timesheet[];
  weekStart: string;
  className?: string;
}

/**
 * Compact 7-day status indicator that sits next to the week date range in
 * the page header. Replaces the single aggregated "Partial" / "Submitted" /
 * etc. badge so partial-week submissions (Mon–Wed submitted, Thu–Fri still
 * draft) read at a glance instead of collapsing to one summary word.
 *
 * Per-day precedence matches the page-level rollup: failed > rejected >
 * submitted > approved > draft > empty. Tooltip on each cell carries the
 * day label, status, entry count, and total hours.
 */
export function TimesheetWeekStatusStrip({ entries, weekStart, className }: Props) {
  const cells = deriveDayCells(entries, weekStart);
  return (
    <TooltipProvider delayDuration={200}>
      <div className={cn('inline-flex items-center gap-1', className)}>
        {cells.map((cell) => {
          const style = STATUS_STYLES[cell.status];
          const day = shortDayName(cell.date);
          const tooltip =
            cell.status === 'empty'
              ? `${day}: empty`
              : `${day}: ${style.label} · ${cell.count} entr${cell.count === 1 ? 'y' : 'ies'} · ${tooltipHours(cell.hours)}`;
          return (
            <Tooltip key={cell.date}>
              <TooltipTrigger asChild>
                <span className="inline-flex flex-col items-center gap-0.5">
                  <span className="text-[9px] uppercase tracking-wide text-muted-foreground">
                    {day.slice(0, 1)}
                  </span>
                  <span
                    className={cn('inline-block size-2 rounded-full', style.dot)}
                    role="img"
                    aria-label={tooltip}
                  />
                </span>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
